type Listener = () => void

class CheckboxStore {
  private checked = new Set<string>()
  private registered = new Set<string>()

  private checkboxListeners = new Map<string, Set<Listener>>()
  private allListeners = new Set<Listener>()

  private checkedIdsSnapshot: string[] = []

  private updateSnapshots() {
    this.checkedIdsSnapshot = [...this.checked]
  }

  // ---------- subscribe ----------

  subscribeCheckbox = (id: string, listener: Listener) => {
    let listeners = this.checkboxListeners.get(id)

    if (!listeners) {
      listeners = new Set()
      this.checkboxListeners.set(id, listeners)
    }

    listeners.add(listener)

    return () => {
      listeners!.delete(listener)

      if (listeners!.size === 0) {
        this.checkboxListeners.delete(id)
      }
    }
  }

  subscribeAll = (listener: Listener) => {
    this.allListeners.add(listener)

    return () => {
      this.allListeners.delete(listener)
    }
  }

  // ---------- notify ----------

  private notifyCheckbox(id: string) {
    this.checkboxListeners.get(id)?.forEach((listener) => listener())
  }

  private notifyAll() {
    this.allListeners.forEach((listener) => listener())
  }

  // ---------- register ----------

  register(id: string) {
    this.registered.add(id)
  }

  unregister(id: string) {
    const checkedChanged = this.checked.delete(id)
    const registeredChanged = this.registered.delete(id)

    if (checkedChanged) {
      this.notifyCheckbox(id)
    }

    if (registeredChanged) {
      this.notifyAll()
    }
  }

  // ---------- getters ----------

  isChecked(id: string) {
    return this.checked.has(id)
  }

  areAllChecked() {
    return this.registered.size > 0 && this.checked.size === this.registered.size
  }

  // ---------- actions ----------

  setChecked(id: string, checked: boolean) {
    const oldValue = this.checked.has(id)

    if (oldValue === checked) {
      return
    }

    if (checked) {
      this.checked.add(id)
    } else {
      this.checked.delete(id)
    }

    this.updateSnapshots()
    this.notifyCheckbox(id)
    this.notifyAll()
  }

  toggle(id: string) {
    this.setChecked(id, !this.checked.has(id))
  }

  setAll(checked: boolean) {
    if (checked) {
      this.checked = new Set(this.registered)
    } else {
      this.checked.clear()
    }

    this.updateSnapshots()

    // уведомляем только зарегистрированные чекбоксы
    this.registered.forEach((id) => {
      this.notifyCheckbox(id)
    })

    this.notifyAll()
  }

  isIndeterminate() {
    return this.checked.size > 0 && this.checked.size < this.registered.size
  }

  getCheckedCount() {
    return this.checked.size
  }

  getCheckedIds() {
    return this.checkedIdsSnapshot
  }

  reset() {
    this.checked.clear()
    this.registered.clear()

    this.updateSnapshots()
    this.notifyAll()
  }
}

export const checkboxStore = new CheckboxStore()

export const checkSimpleStore = {
  subscribeCheckbox: checkboxStore.subscribeCheckbox.bind(checkboxStore),
  subscribeAll: checkboxStore.subscribeAll.bind(checkboxStore),
  register: checkboxStore.register.bind(checkboxStore),
  unregister: checkboxStore.unregister.bind(checkboxStore),
  isChecked: checkboxStore.isChecked.bind(checkboxStore),
  areAllChecked: checkboxStore.areAllChecked.bind(checkboxStore),
  setChecked: checkboxStore.setChecked.bind(checkboxStore),
  toggle: checkboxStore.toggle.bind(checkboxStore),
  setAll: checkboxStore.setAll.bind(checkboxStore),
  isIndeterminate: checkboxStore.isIndeterminate.bind(checkboxStore),
  getCheckedCount: checkboxStore.getCheckedCount.bind(checkboxStore),
  getCheckedIds: checkboxStore.getCheckedIds.bind(checkboxStore),
  reset: checkboxStore.reset.bind(checkboxStore),
}
